import type { BusinessDate } from '@gridfit/core/businessdate'

/**
 * en-GB short date formatting at the display edge (plan's `src/format/` row:
 * "en-GB money, date, number formatting"). `packages/core` bans `Intl`
 * (`no-restricted-globals`), so this cannot live there even though
 * `BusinessDate` itself does — see `money.ts`'s doc comment for the same
 * split applied to money.
 *
 * `timeZone: 'UTC'` is NOT the default and is load-bearing, not decorative.
 * `BusinessDate` is a `YYYY-MM-DD` calendar-day string with no time
 * component (`businessdate/index.ts`'s own doc comment: "NOT a working day —
 * no weekday semantics", and definitely not an instant); it is parsed below
 * as UTC midnight so there is exactly one well-defined instant to format.
 * Formatting that instant in whatever zone the HOST happens to be in —
 * rather than pinning the zone the string was authored in — can shift the
 * displayed day. Verified for real, not assumed (see `date.test.ts`): with
 * the process's timezone forced to `America/New_York` (a negative UTC
 * offset), an Intl.DateTimeFormat with no `timeZone` option reads
 * 2026-08-01's UTC midnight back as "31 Jul 2026" — the day before. A
 * GridFit user whose OS clock happens to be set to a negative-offset zone
 * (travel, a misconfigured machine) would otherwise see every date on
 * screen shifted back a day from what was actually recorded.
 */
const formatter = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  timeZone: 'UTC',
})

export function formatBusinessDate(date: BusinessDate): string {
  return formatter.format(new Date(`${date}T00:00:00.000Z`))
}

/**
 * "14:32 on 1 Aug" — the exact wording the storage-clearance boundary
 * statement uses (design §6.3, `outbox/clearance.ts`). `timeZone:
 * 'Europe/London'`, not `'UTC'` like `formatBusinessDate` above: THIS
 * formats a real INSTANT (a moment the server recorded, `Instant` —
 * "RFC 3339 UTC instant, milliseconds always present" — not a calendar day
 * with no time component), and a site manager reading a clock time on
 * screen expects their own local wall-clock time, not UTC. `BusinessDate`
 * has no time-of-day to shift in the first place (`formatBusinessDate`'s
 * own comment), which is why that formatter pins `'UTC'` instead — the two
 * functions are formatting two different kinds of value, not the same
 * value two different ways.
 *
 * Accepts a plain `string`, not the branded `Instant`: this is called on
 * the wire response from `GET /api/last-received` (`clearance.ts`), which
 * is untrusted, unbranded JSON at that boundary — the same reasoning
 * `useSession`'s `MeResponse` is a plain interface, not a re-validated
 * `Principal`. `new Date(...)` on a malformed string produces `Invalid
 * Date`, which `Intl.DateTimeFormat.format` renders as the literal text
 * "Invalid Date" rather than throwing — visibly wrong rather than a crash,
 * which is the right failure mode for a display-edge formatter fed data it
 * does not control.
 */
const instantTimeFormatter = new Intl.DateTimeFormat('en-GB', {
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
  timeZone: 'Europe/London',
})
const instantDayFormatter = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  timeZone: 'Europe/London',
})

export function formatInstant(instant: string): string {
  const date = new Date(instant)
  return `${instantTimeFormatter.format(date)} on ${instantDayFormatter.format(date)}`
}
